import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import Masthead from "../components/Masthead";
import Kpi from "../components/Kpi";
import Panel from "../components/Panel";
import DistList from "../components/DistList";
import { fmtNum, statusClass } from "../lib/format";

export default function Firewall() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    apiGet("/firewall/summary").then(setD);
  }, []);
  if (!d) return <div className="p-8 opacity-60">Loading…</div>;
  const k = d.kpis;

  return (
    <>
      <Masthead
        module="firewall"
        eyebrow="Network Security · NGFW"
        title="Firewall Health"
        subtitle="FortiGate operational health · log ingestion, throughput, policy, admin & VPN · last 24h"
        meta={<div>Fleet: <strong>{d.fleet_total} FortiGate firewalls</strong><br />as at {d.as_at}</div>}
      />
      <div className="px-[26px] py-5 flex-1">
        <div className="flex items-center justify-between mb-3">
          <div className="cv-section-label !mb-0">Device View · Fleet Health &amp; Throughput</div>
          <span className="text-[11px] small-caps font-bold px-3 py-1 rounded-full tracking-[0.14em]"
                style={{ background: "rgba(213,88,22,0.14)", color: "var(--cv-orange)", border: "1px solid rgba(213,88,22,0.32)" }}>
            NGFW · Device-Centric
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          <Kpi testid="fw-active"  label="Active Firewalls"   value={k.active_firewalls} sub={`of ${d.fleet_total} sending logs`} onClick={() => nav("/drill/firewalls?status=Healthy")} />
          <Kpi testid="fw-silent"  label="Silent Firewalls"   value={d.silent_count} sub="no events received" alert onClick={() => nav("/drill/firewalls?status=Silent")} />
          <Kpi testid="fw-bytes"   label="Bytes Transferred"  value={k.bytes_transferred} sub="fleet throughput · 24h" onClick={() => nav("/drill/firewalls")} />
          <Kpi testid="fw-total"   label="Total Events"       value={k.total_events} sub="all FortiGate logs · 24h" onClick={() => nav("/drill/firewalls")} />
          <Kpi testid="fw-blocked" label="Blocked Events"     value={k.blocked} sub="deny / drop / block" alert onClick={() => nav("/drill/firewalls")} />
          <Kpi testid="fw-allowed" label="Allowed Events"     value={k.allowed} sub="accept / allow / passthrough" onClick={() => nav("/drill/firewalls")} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <Panel testid="panel-fw-list" title="Firewalls · Events Per Device" subtitle="Click any device to drill — Silent devices need investigation" action={
            <button onClick={() => nav("/drill/firewalls")} className="text-[12px] small-caps font-bold underline" style={{ color: "var(--cv-crimson)" }}>see all 644</button>
          }>
            <div className="overflow-x-auto">
              <table className="w-full text-[14px]">
                <thead>
                  <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                    <th className="text-left px-2 py-2 small-caps">Firewall</th>
                    <th className="text-right px-2 py-2 small-caps">Events</th>
                    <th className="text-right px-2 py-2 small-caps">Sessions</th>
                    <th className="text-left px-2 py-2 small-caps">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {d.firewall_sample.slice(0, 12).map((f) => (
                    <tr key={f.id} className="cv-row-clickable" data-testid={`fw-row-${f.id}`} onClick={() => nav(`/drill/firewalls?status=${f.status}`)}>
                      <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}><strong>{f.name}</strong></td>
                      <td className="px-2 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{fmtNum(f.events)}</td>
                      <td className="px-2 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{fmtNum(f.sessions)}</td>
                      <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>
                        <span className={`status-pill ${statusClass(f.status)}`}>{f.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel testid="panel-action-mix" title="Action Distribution" subtitle="Allowed vs blocked vs reset">
            <DistList items={d.action_mix} />
            <div className="text-[13px] mt-4" style={{ color: "var(--cv-charcoal-soft)" }}>
              VPN failures (24h): <strong style={{ color: "var(--cv-crimson)" }}>{fmtNum(d.vpn_failures)}</strong>
            </div>
          </Panel>

          <Panel testid="panel-top-rules" title="Top Firewall Rules by Hit Count" subtitle="High-traffic policies">
            <table className="w-full text-[14px]">
              <tbody>
                {d.rules_top_hits.map((r) => (
                  <tr key={r.id} className="cv-row-clickable">
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>
                      <div className="font-bold">{r.rule}</div>
                      <div className="text-[12px] opacity-60">{r.firewall} · ID {r.policy_id}</div>
                    </td>
                    <td className="px-2 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{fmtNum(r.hits)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <Panel testid="panel-block-rules" title="Top Blocking Rules" subtitle="What's being denied">
            <table className="w-full text-[14px]">
              <thead>
                <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                  <th className="text-left px-2 py-2 small-caps">Firewall</th>
                  <th className="text-left px-2 py-2 small-caps">Blocking Rule</th>
                  <th className="text-right px-2 py-2 small-caps">Blocks</th>
                </tr>
              </thead>
              <tbody>
                {d.rules_top_blocks.map((r) => (
                  <tr key={r.id} className="cv-row-clickable">
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{r.firewall}</td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{r.rule}</td>
                    <td className="px-2 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{fmtNum(r.hits)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <Panel testid="panel-admin" title="Admin Login Events" subtitle="Repeated failures then a success may indicate brute force">
            <table className="w-full text-[14px]">
              <thead>
                <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                  <th className="text-left px-2 py-2 small-caps">Firewall</th>
                  <th className="text-left px-2 py-2 small-caps">User</th>
                  <th className="text-left px-2 py-2 small-caps">Interface</th>
                  <th className="text-left px-2 py-2 small-caps">Status</th>
                </tr>
              </thead>
              <tbody>
                {d.admin_logins.map((a) => (
                  <tr key={a.id} className="cv-row-clickable">
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{a.firewall}</td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}><strong>{a.user}</strong></td>
                    <td className="px-2 py-2 border-b font-mono text-[14px]" style={{ borderColor: "var(--cv-line)" }}>{a.interface}</td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>
                      <span className={`status-pill ${statusClass(a.status)}`}>{a.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel testid="panel-config" title="Configuration Changes" subtitle="Every change should map to an approved request">
            <table className="w-full text-[14px]">
              <thead>
                <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                  <th className="text-left px-2 py-2 small-caps">Time</th>
                  <th className="text-left px-2 py-2 small-caps">Firewall</th>
                  <th className="text-left px-2 py-2 small-caps">User</th>
                  <th className="text-left px-2 py-2 small-caps">Change</th>
                </tr>
              </thead>
              <tbody>
                {d.config_changes.map((c) => (
                  <tr key={c.id} className="cv-row-clickable">
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{c.time}</td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{c.firewall}</td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}><strong>{c.user}</strong></td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{c.change}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <Panel testid="panel-vpn" title="VPN Tunnel Health" subtitle="IPsec/SSL-VPN events and failures">
            <table className="w-full text-[14px]">
              <thead>
                <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                  <th className="text-left px-2 py-2 small-caps">Firewall</th>
                  <th className="text-left px-2 py-2 small-caps">Event</th>
                  <th className="text-left px-2 py-2 small-caps">Type</th>
                  <th className="text-right px-2 py-2 small-caps">Events</th>
                  <th className="text-right px-2 py-2 small-caps">Peers</th>
                </tr>
              </thead>
              <tbody>
                {d.vpn_events.map((v) => (
                  <tr key={v.id} className="cv-row-clickable">
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{v.firewall}</td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{v.event}</td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{v.type}</td>
                    <td className="px-2 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{v.events}</td>
                    <td className="px-2 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{v.peers}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </div>

        <div className="cv-section-label mt-6">System Events</div>
        <Panel testid="panel-system-events" title="HA failover · link-down · conserve-mode" subtitle="High priority operational events">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                <th className="text-left px-3 py-2 small-caps">Firewall</th>
                <th className="text-left px-3 py-2 small-caps">Event</th>
                <th className="text-left px-3 py-2 small-caps">Level</th>
                <th className="text-right px-3 py-2 small-caps">Count</th>
              </tr>
            </thead>
            <tbody>
              {d.system_events.map((s) => (
                <tr key={s.id} className="cv-row-clickable">
                  <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}><strong>{s.firewall}</strong></td>
                  <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{s.event}</td>
                  <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>
                    <span className={`status-pill ${statusClass(s.level)}`}>{s.level}</span>
                  </td>
                  <td className="px-3 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{s.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>
    </>
  );
}
